import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Check, X, Building2 } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-amber-500/12 text-amber-700 border-amber-500/30",
  approved: "bg-emerald-500/12 text-emerald-700 border-emerald-500/30",
  rejected: "bg-rose-500/12 text-rose-700 border-rose-500/30",
};

function typeLabel(type, slug, lang) {
  if (!type) return slug || "—";
  return (lang?.startsWith("ar") && type.name_ar) || type.name_en || type.slug;
}

export function AdminCompanyTypeRequests() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/company-type-requests", { params: { status } });
      setItems(res.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (req, approve, reassign) => {
    setBusy(req.request_id);
    try {
      await client.post(`/admin/company-type-requests/${req.request_id}/decision`, {
        approve, reassign_program: reassign, note: notes[req.request_id] || "",
      });
      toast.success(approve ? t("companyType.adminApproveDone") : t("companyType.adminRejectDone"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-type-requests-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
          {t("companyType.adminTitle")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{t("companyType.adminSubtitle")}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["pending", t("companyType.adminFilterPending")], ["all", t("companyType.adminFilterAll")]].map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setStatus(val)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              status === val ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"
            }`}
            data-testid={`type-requests-filter-${val}`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-32 w-full rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="type-requests-empty">
          {t("companyType.adminEmpty")}
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((req) => (
            <Card key={req.request_id} className="rounded-2xl border border-border p-4 sm:p-5 space-y-4" data-testid={`type-request-${req.request_id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Building2 className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-sm truncate" data-testid={`type-request-company-${req.request_id}`}>
                      {req.company_name || req.company_id}
                    </span>
                    <Badge variant="outline" className={`rounded-full text-[10px] ${STATUS_STYLES[req.status] || ""}`}>
                      {t(`companyType.admin${req.status.charAt(0).toUpperCase()}${req.status.slice(1)}`, { defaultValue: req.status })}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {req.user_name || req.user_email}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                      {t("companyType.adminFrom")}: {typeLabel(req.from_type, req.from_slug, i18n.language) || "—"}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground rtl-flip" />
                    <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 font-medium text-primary" data-testid={`type-request-to-${req.request_id}`}>
                      {typeLabel(req.to_type, req.to_slug, i18n.language)}
                    </span>
                  </div>
                  {req.reason && (
                    <p className="text-xs text-muted-foreground mt-2">
                      <span className="font-medium">{t("companyType.adminReason")}: </span>{req.reason}
                    </p>
                  )}
                </div>
              </div>

              {req.status === "pending" && (
                <div className="space-y-2.5 border-t border-border pt-3.5">
                  <Input
                    className="rounded-xl"
                    placeholder={t("companyType.adminNote")}
                    value={notes[req.request_id] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [req.request_id]: e.target.value }))}
                    data-testid={`type-request-note-${req.request_id}`}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="rounded-xl gap-2"
                      disabled={busy === req.request_id}
                      onClick={() => decide(req, true, true)}
                      data-testid={`type-request-approve-${req.request_id}`}
                    >
                      <Check className="h-3.5 w-3.5" /> {t("companyType.adminApprove")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl"
                      disabled={busy === req.request_id}
                      onClick={() => decide(req, true, false)}
                      data-testid={`type-request-approve-only-${req.request_id}`}
                    >
                      {t("companyType.adminApproveOnly")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl gap-2 text-rose-600 hover:text-rose-700"
                      disabled={busy === req.request_id}
                      onClick={() => decide(req, false, false)}
                      data-testid={`type-request-reject-${req.request_id}`}
                    >
                      <X className="h-3.5 w-3.5" /> {t("companyType.adminReject")}
                    </Button>
                  </div>
                </div>
              )}
              {req.status !== "pending" && (
                <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
                  {t("companyType.adminDecided")}: {req.decided_by || "—"}
                  {req.decision_note ? ` · ${req.decision_note}` : ""}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminCompanyTypeRequests;
